import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Star, ShoppingBag, Truck, ShieldCheck, RefreshCcw, Sparkles,
  Gift, Copy, Check, Phone, Mail, MessageCircle, FileText, MapPin, Zap, Flame, ArrowLeft,
} from 'lucide-react';
import { useData } from '../context/DataContext';
import { useLang } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';

const OFFERS = [
  { code: 'BHAKTI100', off: 100, min: 1200, note: 'Above ₹1200' },
  { code: 'BHAKTI50', off: 50, min: 700, note: 'Above ₹700' },
];

const TRUST_BADGES = [
  { icon: ShieldCheck, label_en: '100% Authentic', label_hi: '100% असली' },
  { icon: Sparkles, label_en: 'Energized', label_hi: 'ऊर्जावान' },
  { icon: Truck, label_en: 'Fast Shipping', label_hi: 'तेज़ शिपिंग' },
  { icon: RefreshCcw, label_en: 'Easy Returns', label_hi: 'आसान वापसी' },
];

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, settings } = useData();
  const { t, lang } = useLang();
  const { addItem, setOpen: setCartOpen } = useCart();
  const { toast } = useToast();

  const product = useMemo(() => products.find((p) => p.id === id), [products, id]);

  const [imgIdx, setImgIdx] = useState(0);
  const [abhimantrit, setAbhimantrit] = useState(false);
  const [pincode, setPincode] = useState('');
  const [pincodeMsg, setPincodeMsg] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="text-nv-wine text-lg font-semibold">
          {lang === 'hi' ? 'उत्पाद नहीं मिला' : 'Product not found'}
        </p>
        <Link to="/shop" className="mt-4 inline-flex items-center gap-2 text-nv-gold hover:underline">
          <ArrowLeft className="w-4 h-4" /> {lang === 'hi' ? 'दुकान पर जाएँ' : 'Back to Shop'}
        </Link>
      </div>
    );
  }

  const images = [product.image].filter(Boolean);
  const discount =
    product.originalPrice > product.price
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0;
  const finalPrice = product.price + (abhimantrit ? 101 : 0);
  const stock = product.stock ?? 0;
  const outOfStock = stock <= 0;
  const name = lang === 'hi' ? product.name_hi || product.name_en : product.name_en;
  const description = lang === 'hi' ? product.description_hi || product.description_en : product.description_en;

  const tags = [
    ...(product.purposes || []),
    product.category,
    'authentic',
    'energised',
    'certified',
  ];

  const addToCartWithOptions = () => {
    if (outOfStock) return;
    const payload = {
      ...product,
      id: abhimantrit ? `${product.id}__abh` : product.id,
      name_en: abhimantrit ? `${product.name_en} + Abhimantrit (Name & Gotra)` : product.name_en,
      name_hi: abhimantrit ? `${product.name_hi || product.name_en} + अभिमंत्रित (नाम व गोत्र)` : product.name_hi,
      price: finalPrice,
    };
    addItem(payload);
    toast({ title: lang === 'hi' ? 'टोकरी में जोड़ा गया' : 'Added to cart' });
  };

  const buyOnWhatsapp = () => {
    const phone = (settings.whatsapp || '917250412100').replace(/\D/g, '');
    const line1 = lang === 'hi'
      ? `🙏 नमस्ते! मैं यह उत्पाद खरीदना चाहता हूँ:\n\n${name}\n₹${finalPrice}`
      : `🙏 Namaste! I want to buy this product:\n\n${name}\n₹${finalPrice}`;
    const line2 = abhimantrit ? (lang === 'hi' ? '\n(अभिमंत्रित संकल्प सहित — नाम व गोत्र लिया जाए)' : '\n(With Abhimantrit sankalp — please take name & gotra)') : '';
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(line1 + line2)}`, '_blank');
  };

  const copyPromo = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    toast({ title: `${lang === 'hi' ? 'कोड कॉपी हो गया' : 'Code copied'}: ${code}` });
    setTimeout(() => setCopied(''), 2000);
  };

  const checkPincode = () => {
    if (!/^\d{6}$/.test(pincode)) {
      setPincodeMsg(lang === 'hi' ? 'कृपया 6 अंकों का पिनकोड डालें' : 'Please enter a valid 6-digit pincode');
      return;
    }
    setPincodeMsg(
      lang === 'hi'
        ? `✓ इस पिनकोड पर डिलीवरी उपलब्ध है • 4-7 दिन में पहुँचेगा`
        : `✓ Delivery available at ${pincode} • Arrives in 4-7 days`
    );
  };

  const specs = [
    { k_en: 'Category', k_hi: 'श्रेणी', v: product.category },
    { k_en: 'Purposes', k_hi: 'उद्देश्य', v: (product.purposes || []).join(', ') || '—' },
    { k_en: 'Abhimantrit', k_hi: 'अभिमंत्रित', v: 'Baba Baidyanath Dham' },
    { k_en: 'Origin', k_hi: 'मूल स्थान', v: 'Bharat' },
    { k_en: 'Stock', k_hi: 'स्टॉक', v: outOfStock ? 'Out of stock' : `${stock} units` },
  ];

  return (
    <div className="min-h-screen bg-[#FFF9F0] pb-32 md:pb-10" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-nv-muted">
        <Link to="/" className="hover:text-nv-gold">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/shop" className="hover:text-nv-gold">Shop</Link>
        <span className="mx-2">/</span>
        <span className="text-nv-wine font-medium truncate inline-block max-w-[200px] align-bottom">{name}</span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <div className="grid md:grid-cols-2 gap-6 md:gap-12">
          {/* LEFT: Image gallery */}
          <div>
            <div className="relative aspect-square bg-white rounded-2xl overflow-hidden border border-nv-gold/20 shadow-sm">
              <img
                src={images[imgIdx]}
                alt={name}
                className="w-full h-full object-contain"
                data-testid="product-main-image"
              />
              <div className="absolute top-3 left-3 flex gap-2">
                <span className="bg-green-600 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> CERTIFIED
                </span>
                {discount > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                    {discount}% OFF
                  </span>
                )}
              </div>
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setImgIdx((i) => (i - 1 + images.length) % images.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setImgIdx((i) => (i + 1) % images.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                {images.map((im, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 ${
                      i === imgIdx ? 'border-nv-gold' : 'border-transparent'
                    }`}
                  >
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: Details */}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-nv-wine mb-1" data-testid="product-title">
              {name}
            </h1>
            <p className="text-sm text-nv-gold font-medium mb-3">100% Authentic & Certified</p>

            {product.rating > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-1 bg-green-600 text-white px-2 py-0.5 rounded text-xs font-bold">
                  {product.rating} <Star className="w-3 h-3 fill-white" />
                </div>
                <span className="text-sm text-nv-muted">
                  {product.ratingCount || 0} {lang === 'hi' ? 'समीक्षाएँ' : 'reviews'}
                </span>
              </div>
            )}

            {/* Price block */}
            <div className="bg-white border border-nv-gold/20 rounded-2xl p-4 md:p-5 mb-4 shadow-sm">
              {discount > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                    LIMITED TIME DEAL
                  </span>
                  <span className="text-red-600 text-xs font-bold">{discount}% OFF</span>
                </div>
              )}
              <div className="flex items-baseline gap-3 mb-1">
                <span className="text-3xl md:text-4xl font-bold text-nv-wine" data-testid="product-price">
                  ₹{finalPrice.toLocaleString('en-IN')}
                </span>
                {product.originalPrice > product.price && (
                  <span className="text-base text-stone-400 line-through">
                    ₹{product.originalPrice.toLocaleString('en-IN')}
                  </span>
                )}
              </div>
              <p className="text-xs text-nv-muted mb-3">
                {lang === 'hi'
                  ? 'GST शामिल • ₹299 से ऊपर मुफ़्त डिलीवरी'
                  : 'GST included • Free delivery on orders above ₹299'}
              </p>

              {!outOfStock && stock < 100 && (
                <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mb-3">
                  <Flame className="w-4 h-4 text-orange-600 shrink-0" />
                  <p className="text-xs font-semibold text-orange-800">
                    {lang === 'hi'
                      ? `केवल ${stock} इकाइयाँ शेष!`
                      : `Only ${stock} units left today!`}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <Gift className="w-4 h-4 text-green-700 shrink-0" />
                <p className="text-xs font-semibold text-green-800">
                  {lang === 'hi' ? 'मुफ़्त उपहार ₹449 का' : 'FREE GIFT Worth ₹449'}
                </p>
              </div>
            </div>

            {/* Abhimantrit personalization */}
            <label
              className={`flex items-start gap-3 border rounded-2xl p-4 mb-4 cursor-pointer transition ${
                abhimantrit ? 'border-nv-gold bg-nv-gold/10' : 'border-nv-gold/20 bg-white hover:border-nv-gold/50'
              }`}
              data-testid="abhimantrit-toggle"
            >
              <input
                type="checkbox"
                checked={abhimantrit}
                onChange={(e) => setAbhimantrit(e.target.checked)}
                className="mt-1 w-5 h-5 accent-nv-gold"
              />
              <div className="flex-1">
                <p className="text-sm font-bold text-nv-wine flex items-center gap-2">
                  ॐ {lang === 'hi' ? 'नाम व गोत्र सहित अभिमंत्रित करें' : 'ABHIMANTRIT WITH NAME & GOTRA'}
                  <span className="text-red-600 font-bold">(+ ₹101)</span>
                </p>
                <p className="text-xs text-nv-muted mt-0.5">
                  {lang === 'hi'
                    ? 'ऊर्जावान करते समय आपका नाम व गोत्र संकल्प में लिया जाएगा।'
                    : 'Your name & gotra will be taken in sankalp while energizing.'}
                </p>
              </div>
            </label>

            {/* Trust badges grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {TRUST_BADGES.map((b, i) => (
                <div
                  key={i}
                  className="bg-white border border-nv-gold/20 rounded-xl p-3 flex items-center gap-2"
                >
                  <span className="w-8 h-8 rounded-full bg-nv-gold/15 flex items-center justify-center text-nv-gold shrink-0">
                    <b.icon className="w-4 h-4" />
                  </span>
                  <span className="text-xs font-semibold text-nv-wine">
                    {lang === 'hi' ? b.label_hi : b.label_en}
                  </span>
                </div>
              ))}
            </div>

            {/* CTAs — inline (desktop) */}
            <div className="hidden md:block space-y-3">
              <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-3 flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wider text-yellow-800 font-bold">
                    {lang === 'hi' ? 'कैश ऑन डिलीवरी' : 'Cash on Delivery'}
                  </p>
                  <p className="text-xs text-yellow-700 mt-0.5">
                    {lang === 'hi'
                      ? `अभी ₹${Math.max(49, Math.round(finalPrice * 0.03))} दें • बाकी डिलीवरी पर`
                      : `Pay ₹${Math.max(49, Math.round(finalPrice * 0.03))} now • rest on delivery`}
                  </p>
                </div>
                <button
                  onClick={addToCartWithOptions}
                  disabled={outOfStock}
                  data-testid="add-to-cart-desktop"
                  className="px-4 py-2 rounded-full bg-white border border-yellow-500 text-yellow-800 font-semibold text-sm hover:bg-yellow-100 disabled:opacity-50"
                >
                  {lang === 'hi' ? 'टोकरी में' : 'Add to Cart'}
                </button>
              </div>
              <button
                onClick={buyOnWhatsapp}
                disabled={outOfStock}
                data-testid="buy-now-desktop"
                className="w-full bg-gradient-to-r from-nv-wine to-red-700 hover:from-red-800 hover:to-red-800 text-nv-cream font-bold py-3.5 rounded-full flex items-center justify-center gap-2 shadow-md transition disabled:opacity-50"
              >
                <Zap className="w-4 h-4" />
                {lang === 'hi' ? 'अभी खरीदें और ₹25 पाएँ' : 'Pay Online and get ₹25/- off'}
              </button>
              <div className="flex items-center justify-center gap-2 opacity-70">
                <span className="text-[10px] text-nv-muted uppercase tracking-wider">Accepted:</span>
                <span className="text-[10px] font-semibold text-nv-wine">UPI • VISA • Mastercard • Rupay • Netbanking</span>
              </div>
            </div>
          </div>
        </div>

        {/* OFFERS */}
        <section className="mt-10 md:mt-14">
          <h2 className="text-lg font-bold text-nv-wine mb-3 uppercase tracking-wider">
            {lang === 'hi' ? 'ऑफ़र' : 'Offers'}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-white shrink-0">
                <Zap className="w-5 h-5" />
              </span>
              <div>
                <p className="text-sm font-bold text-yellow-900">
                  {lang === 'hi' ? 'प्रीपेड ऑर्डर पर ₹25 छूट' : '₹25 OFF on Prepaid Orders'}
                </p>
                <p className="text-xs text-yellow-800 mt-0.5">
                  {lang === 'hi' ? 'ऑनलाइन भुगतान पर स्वयं लागू' : 'Auto-applied when you pay online'}
                </p>
              </div>
            </div>
            <div className="bg-green-50 border border-green-300 rounded-xl p-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white shrink-0">
                <Truck className="w-5 h-5" />
              </span>
              <div>
                <p className="text-sm font-bold text-green-900">
                  {lang === 'hi' ? 'हमेशा मुफ़्त डिलीवरी' : 'Free delivery always'}
                </p>
                <p className="text-xs text-green-800 mt-0.5">
                  {lang === 'hi' ? '₹299 से ऊपर के ऑर्डर पर' : 'On orders above ₹299'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* PROMO CODES */}
        <section className="mt-8">
          <h2 className="text-lg font-bold text-nv-wine mb-3 uppercase tracking-wider">
            {lang === 'hi' ? 'प्रोमो कोड' : 'Promo Codes'}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {OFFERS.map((o) => (
              <div
                key={o.code}
                className="bg-white border-2 border-dashed border-green-500 rounded-xl p-4 flex items-center justify-between gap-3"
              >
                <div>
                  <p className="text-base font-bold text-green-700 tracking-wider">{o.code}</p>
                  <p className="text-xs text-nv-muted mt-0.5">
                    ₹{o.off} OFF • {o.note} • {lang === 'hi' ? 'सीमित समय' : 'Limited time'}
                  </p>
                </div>
                <button
                  onClick={() => copyPromo(o.code)}
                  data-testid={`copy-promo-${o.code}`}
                  className="flex items-center gap-1 border border-green-500 text-green-700 font-semibold text-xs px-3 py-1.5 rounded-full hover:bg-green-50"
                >
                  {copied === o.code ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === o.code ? (lang === 'hi' ? 'कॉपी' : 'Copied') : (lang === 'hi' ? 'कॉपी' : 'Copy')}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* DELIVERY */}
        <section className="mt-8">
          <h2 className="text-lg font-bold text-nv-wine mb-3 uppercase tracking-wider">
            {lang === 'hi' ? 'डिलीवरी' : 'Delivery'}
          </h2>
          <div className="bg-white border border-nv-gold/20 rounded-xl p-4">
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 border border-nv-gold/30 rounded-lg px-3 py-2 bg-nv-cream/50">
                <MapPin className="w-4 h-4 text-nv-gold shrink-0" />
                <input
                  type="text"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder={lang === 'hi' ? 'पिनकोड डालें' : 'Enter Pincode'}
                  className="flex-1 bg-transparent focus:outline-none text-sm"
                  data-testid="pincode-input"
                />
              </div>
              <button
                onClick={checkPincode}
                data-testid="pincode-check"
                className="btn-orange px-5 rounded-lg font-semibold text-sm"
              >
                {lang === 'hi' ? 'जाँचें' : 'Check'}
              </button>
            </div>
            {pincodeMsg && (
              <p className={`text-xs mt-2 ${pincodeMsg.startsWith('✓') ? 'text-green-700' : 'text-red-600'}`} data-testid="pincode-msg">
                {pincodeMsg}
              </p>
            )}
            <p className="text-xs text-nv-muted mt-3">
              {lang === 'hi'
                ? '✓ पूरे भारत में डिलीवरी उपलब्ध • 7 दिनों में आसान रिप्लेसमेंट'
                : '✓ Pan India delivery supported • Easy replacement within 7 days'}
            </p>
          </div>
        </section>

        {/* SPECS + TAGS */}
        <section className="mt-8 grid md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-lg font-bold text-nv-wine mb-3 uppercase tracking-wider">
              {lang === 'hi' ? 'विशेषताएँ' : 'Product Specifications'}
            </h2>
            <div className="bg-white border border-nv-gold/20 rounded-xl p-4 space-y-2">
              {specs.map((s, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-nv-gold/10 pb-2 last:border-0 last:pb-0">
                  <span className="text-nv-muted capitalize">{lang === 'hi' ? s.k_hi : s.k_en}</span>
                  <span className="text-nv-wine font-medium capitalize text-right">{s.v}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-lg font-bold text-nv-wine mb-3 uppercase tracking-wider">
              {lang === 'hi' ? 'टैग' : 'Tags'}
            </h2>
            <div className="flex flex-wrap gap-2">
              {tags.map((tg, i) => (
                <span
                  key={i}
                  className="text-xs font-medium bg-white border border-nv-gold/30 text-nv-wine capitalize px-3 py-1.5 rounded-full hover:bg-nv-gold/10 cursor-pointer"
                >
                  #{tg}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* NEED HELP */}
        <section className="mt-8">
          <h2 className="text-lg font-bold text-nv-wine mb-3 uppercase tracking-wider">
            {lang === 'hi' ? 'सहायता चाहिए?' : 'Need Help?'}
          </h2>
          <div className="bg-white border border-nv-gold/20 rounded-xl p-4">
            <p className="text-xs text-nv-muted mb-3">
              {lang === 'hi'
                ? 'सोम - शनि, 10 AM - 7 PM • ऑर्डर, भुगतान और उत्पाद मार्गदर्शन के लिए Navavidha सहायता।'
                : 'Mon to Sat, 10 AM - 7 PM • Navavidha Support for orders, payments & product guidance.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <a
                href={`https://wa.me/${(settings.whatsapp || '917250412100').replace(/\D/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 border border-green-300 text-green-700 rounded-lg px-3 py-2 hover:bg-green-50 text-sm"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a
                href={`tel:${settings.contactPhone || '+917250412100'}`}
                className="flex items-center gap-2 border border-nv-gold/30 text-nv-wine rounded-lg px-3 py-2 hover:bg-nv-gold/10 text-sm"
              >
                <Phone className="w-4 h-4" /> {settings.contactPhone || '+91 7250412100'}
              </a>
              <a
                href={`mailto:${settings.contactEmail || 'navavidhabhakti001@gmail.com'}`}
                className="flex items-center gap-2 border border-nv-gold/30 text-nv-wine rounded-lg px-3 py-2 hover:bg-nv-gold/10 text-sm truncate"
              >
                <Mail className="w-4 h-4 shrink-0" />
                <span className="truncate">{settings.contactEmail || 'navavidhabhakti001@gmail.com'}</span>
              </a>
            </div>
          </div>
        </section>

        {/* DESCRIPTION */}
        {description && (
          <section className="mt-8">
            <h2 className="text-lg font-bold text-nv-wine mb-3 uppercase tracking-wider flex items-center gap-2">
              <FileText className="w-4 h-4" /> {lang === 'hi' ? 'विवरण' : 'Description'}
            </h2>
            <div className="bg-white border border-nv-gold/20 rounded-xl p-5 text-sm leading-relaxed text-stone-700 whitespace-pre-line">
              {description}
            </div>
          </section>
        )}
      </div>

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-nv-gold/30 shadow-2xl p-3 flex gap-2 z-40" data-testid="sticky-cta">
        <button
          onClick={addToCartWithOptions}
          disabled={outOfStock}
          data-testid="add-to-cart-mobile"
          className="flex-1 border-2 border-nv-gold text-nv-wine font-bold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <ShoppingBag className="w-4 h-4" />
          {lang === 'hi' ? 'टोकरी' : 'Add to Cart'}
        </button>
        <button
          onClick={buyOnWhatsapp}
          disabled={outOfStock}
          data-testid="buy-now-mobile"
          className="flex-1 bg-gradient-to-r from-nv-wine to-red-700 text-nv-cream font-bold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Zap className="w-4 h-4" />
          {lang === 'hi' ? 'खरीदें' : 'Buy Now'}
        </button>
      </div>
    </div>
  );
}
